import { Button, Space, Tooltip } from '@oscar/theme';
import {
  ActionButtonTable,
  FilterAreaSectionColumnTable,
  FilterDateRangeColumnTable,
  FilterRoleUserManagement,
  FilterApplicableService,
  FilterStatusColumnTable,
  FilterStatusColumnTableHome,
  StatusTagColumnTable,
} from 'common/components';
import { FORMAT_DATE } from 'common/constants';
import { Roles, useAuth } from 'features/auth';
import moment from 'moment';
import { REQUEST_TYPE } from 'common/constants/insulation';
import { Link } from 'react-router-dom';
import { ActionButtons } from '@oscar/table';
import EditDeleteButtonsTable from 'common/components/EditDeleteButtonsTable';
import { InfoCircleOutlined } from '@ant-design/icons';

export const transformRequest = (data: any) => {
  const { filters, currentRole, ...remain } = data;
  const roleId = Roles.indexOf(currentRole?.[0]) + 1;
  const dataConfig = {
    ...remain,
    filter: {
      ...filters,
      startDate: filters?.dateRange?.[0],
      endDate: filters?.dateRange?.[1],
      listArea: filters?.areaSection?.[0],
      listSection: filters?.areaSection?.[1],
      listStatus: filters?.status,
      currentRole: roleId,
    },
  };
  return dataConfig;
};

export const transformResponse = (dataResponse: any) => {
  const { data = [], metadata, ...remain } = JSON.parse(dataResponse);
  const { filter, ...meta } = metadata;
  const {
    startDate = '',
    endDate = '',
    listStatus,
    listArea,
    listSection,
    ...remainFiltered
  } = filter;
  return {
    ...remain,
    metadata: {
      ...meta,
      filters: {
        ...remainFiltered,
        status: listStatus,
        dateRange: startDate || endDate ? [startDate, endDate] : [],
        areaSection: listArea || listSection ? [listArea, listSection] : [],
      },
    },
    data: data.map((item: any) => {
      return {
        ...item,
        idScaffoldNo: {
          id: item.requestId,
          scaffoldRefNo: item.scaffoldRefNo,
        },
        areaSection: {
          areaLabel: item.areaLabel,
          sectionLabel: item.sectionLabel,
        },
        dateRange: {
          startDate: item.startDate,
          endDate: item.endDate,
        },
      };
    }),
  };
};

export const transReqUserManagement = (data: any) => {
  const { filters, sorter, ...remain } = data;

  const dataConfig = {
    ...remain,
    filters: {
      ...filters,
      role: filters?.listRole,
    },
    sorters: {
      field: sorter?.field === 'nameAndEmail' ? 'name' : sorter?.field,
      order:
        sorter?.order === 'ascend'
          ? '0'
          : sorter?.order === 'descend'
          ? '1'
          : null,
    },
  };
  return dataConfig;
};

export const transResUserManagement = (dataResponse: any) => {
  const { data = [], metadata, ...remain } = JSON.parse(dataResponse);
  const { filters, sorters, ...meta } = metadata;
  const { name, email, ...remainFiltered } = filters;
  return {
    ...remain,
    metadata: {
      ...meta,
      filters: {
        ...remainFiltered,
        listRole: filters?.role,
      },
      sorter: {
        order:
          sorters.order === '0'
            ? 'ascend'
            : sorters.order === '1'
            ? 'descend'
            : null,
        field: sorters.field,
      },
    },
    data: data?.map((item: any) => {
      return {
        ...item,
        nameAndEmail: {
          name: item?.name,
          email: item?.email,
        },
      };
    }),
  };
};

export const transReqServiceProvider = (data: any) => {
  const { filters, sorter, ...remain } = data;
  const dataConfig = {
    ...remain,
    filters: {
      keyWord: filters?.keyWord || '',
      areas: filters?.areas || [],
      applicableServices: filters?.applicableServices || [],
    },
    sorters: {
      field: sorter?.column?.key,
      order:
        sorter?.order === 'ascend' ? 0 : sorter?.order === 'descend' ? 1 : -1,
    },
  };
  return dataConfig;
};

export const transResServiceProvider = (dataResponse: any) => {
  const { data = [], metadata, ...remain } = JSON.parse(dataResponse);
  const { filters, sorters, ...meta } = metadata;
  const { areas, applicableServices, ...remainFiltered } = filters;
  return {
    ...remain,
    metadata: {
      ...meta,
      filters: {
        ...remainFiltered,
        areas: areas === null ? [] : areas,
        applicableServices:
          applicableServices === null ? [] : applicableServices,
      },
      sorter: {
        order:
          sorters.order === 0
            ? 'ascend'
            : sorters.order === 1
            ? 'descend'
            : null,
        field: sorters.field,
      },
    },
    data: data?.data,
  };
};

export const columns = [
  {
    title: 'ID',
    dataIndex: 'idScaffoldNo',
    key: 'idScaffoldNo',
    render: (text: any) => (
      <>
        <p className="table-column-text-bold">{text?.id}</p>
        <p className="table-column-section-label">{text?.scaffoldRefNo}</p>
      </>
    ),
  },
  {
    title: 'SAP ID',
    key: 'sapID',
    dataIndex: 'moNumber',
    width: '12%',
    render: (text: any, record: any) => {
      //console.log(record.moNumber);
      let monumber = record.moNumber;
      let notificationId = record.sapNotificationID;

      if (monumber !== null && monumber !== undefined) {
        // monumber = monumber.replace(/[;\s]+$/, '');
        monumber = monumber.replace(/;/g, ',');

        let values = monumber.split(',');
        values = Array.from(
          new Set(values.map((value: string) => value.slice(-8))),
        );
        monumber = values.join('\n');
      }
      // console.log(monumber);

      if (notificationId !== null && notificationId !== undefined) {
        // notificationId = notificationId.replace(/[;\s]+$/, '');
        notificationId = notificationId.replace(/;/g, ',');

        let values = notificationId.split(',');
        values = Array.from(
          new Set(values.map((value: string) => value.slice(-8))),
        );
        notificationId = values.join('\n');
      }
      // console.log(notificationId);

      if (!/\d/.test(record.moNumber) && record.sapNotificationID != null) {
        return (
          <span className="table-column-text-bold">
            {`Notification ID: ${notificationId}`}
          </span>
        );
      } else if (record.moNumber) {
        return (
          <span className="table-column-text-bold">
            {`MO Number: ${monumber}`}
          </span>
        );
      } else {
        return <span className="table-column-text-bold">NA</span>;
      }
    },
  },
  {
    title: 'Description',
    dataIndex: 'description',
    width: '20%',
    key: 'description',
    render: (description: any) => (
      <div style={{ width: '20rem', wordWrap: 'break-word' }}>
        <Tooltip placement="topLeft" title={description}>
          <span className="table-column-text-bold">{description}</span>
        </Tooltip>
      </div>
    ),
  },
  {
    title: 'Area/Section',
    dataIndex: 'areaSection',
    key: 'areaSection',
    width: '15%',
    filterDropdown: FilterAreaSectionColumnTable,
    render: (text: any) => (
      <>
        <p className="table-column-text-bold">{text?.areaLabel}</p>
        <p className="table-column-section-label">{text?.sectionLabel}</p>
      </>
    ),
  },
  {
    title: 'Date Range',
    dataIndex: 'dateRange',
    key: 'dateRange',
    width: '10%',
    filterDropdown: FilterDateRangeColumnTable,
    render: (date: any) =>
      date ? (
        <>
          <p className="table-column-date-range">
            {date?.startDate &&
              moment(date.startDate).format(FORMAT_DATE.SHORT_MONTH_DATE)}
          </p>
          <p className="table-column-date-range">
            {date?.endDate &&
              moment(date.endDate).format(FORMAT_DATE.SHORT_MONTH_DATE)}
          </p>
        </>
      ) : null,
    filters: [
      { text: 'Male', value: 'male' },
      { text: 'Female', value: 'female' },
    ],
  },
  {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
    filterDropdown: FilterStatusColumnTable,
    render: StatusTagColumnTable,
    width: '20%',
  },
  {
    title: 'Action',
    key: 'action',
    width: '10%',
    render: (record: any) => {
      const scaffold_view_url = process.env.REACT_APP_VIEW_SCAFFOLD_URL;
      const scaffold_edit_url = process.env.REACT_APP_EDIT_SCAFFOLD_URL;
      return (
        <div className="table-column-action">
          <Space size="middle">
            <Button type="primary">
              {record?.requestId.slice(0, 3).toLowerCase() === 'scf' ? (
                <a
                  onClick={() =>
                    window.open(
                      `${scaffold_view_url}${record?.scaffoldGuid}`,
                      '_blank',
                    )
                  }
                >
                  View
                </a>
              ) : (
                <Link
                  to={`/insulation/view/${record?.requestId}?type=${
                    Object.values(REQUEST_TYPE)?.[record?.service - 1]
                  }`}
                >
                  View
                </Link>
              )}
              {/* <Link
                to={
                  record?.requestId.slice(0, 3).toLowerCase() === 'scf'
                    ? 'https://sisfolds.petronas.com/scaffold-request/list-scaffold-request'
                    : `/insulation/view/${record?.requestId}?type=${
                        Object.values(REQUEST_TYPE)?.[record?.service - 1]
                      }`
                }
                target={
                  record?.requestId.slice(0, 3).toLowerCase() === 'scf'
                    ? '_blank'
                    : '_self'
                }
              >
                View
              </Link> */}
            </Button>

            {record?.requestId.slice(0, 3).toLowerCase() === 'scf' ? (
              <Button>
                <a
                  onClick={() =>
                    window.open(
                      `${scaffold_edit_url}${record?.scaffoldGuid}`,
                      '_blank',
                    )
                  }
                >
                  Edit
                </a>
              </Button>
            ) : (
              <ActionButtons uniqueKey={record?.requestId} hotReload>
                {actionProps => (
                  <ActionButtonTable
                    record={record}
                    roleButton={RoleButton()}
                    actionProps={actionProps}
                  />
                )}
              </ActionButtons>
            )}
          </Space>
        </div>
      );
    },
  },
];

export const columnsHomeTab = [
  {
    title: 'ID',
    dataIndex: 'idScaffoldNo',
    key: 'idScaffoldNo',
    render: (text: any) => (
      <>
        <p className="table-column-text-bold">{text?.id}</p>
        <p className="table-column-section-label">{text?.scaffoldRefNo}</p>
      </>
    ),
  },
  {
    title: 'SAP ID',
    key: 'sapID',
    dataIndex: 'moNumber',
    width: '12%',
    render: (text: any, record: any) => {
      //console.log(record.moNumber);
      let monumber = record.moNumber;
      let notificationId = record.sapNotificationID;

      if (monumber !== null && monumber !== undefined) {
        // monumber = monumber.replace(/[;\s]+$/, '');
        monumber = monumber.replace(/;/g, ',');

        let values = monumber.split(',');
        values = Array.from(
          new Set(values.map((value: string) => value.slice(-8))),
        );
        monumber = values.join('\n');
      }
      // console.log(monumber);

      if (notificationId !== null && notificationId !== undefined) {
        // notificationId = notificationId.replace(/[;\s]+$/, '');
        notificationId = notificationId.replace(/;/g, ',');

        let values = notificationId.split(',');
        values = Array.from(
          new Set(values.map((value: string) => value.slice(-8))),
        );
        notificationId = values.join('\n');
      }
      // console.log(notificationId);

      if (!/\d/.test(record.moNumber) && record.sapNotificationID != null) {
        return (
          <span className="table-column-text-bold">
            {`Notification ID: ${notificationId}`}
          </span>
        );
      } else if (record.moNumber) {
        return (
          <span className="table-column-text-bold">
            {`MO Number: ${monumber}`}
          </span>
        );
      } else {
        return <span className="table-column-text-bold">NA</span>;
      }
    },
  },
  {
    title: 'Description',
    dataIndex: 'description',
    width: '20%',
    key: 'description',
    render: (description: any) => (
      <Tooltip placement="topLeft" title={description}>
        <span className="table-column-text-bold">{description}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Area/Section',
    dataIndex: 'areaSection',
    key: 'areaSection',
    width: '15%',
    filterDropdown: FilterAreaSectionColumnTable,
    render: (text: any) => (
      <>
        <p className="table-column-text-bold">{text?.areaLabel}</p>
        <p className="table-column-section-label">{text?.sectionLabel}</p>
      </>
    ),
  },
  {
    title: 'Date Range',
    dataIndex: 'dateRange',
    key: 'dateRange',
    width: '10%',
    filterDropdown: FilterDateRangeColumnTable,
    render: (date: any) =>
      date ? (
        <>
          <p className="table-column-date-range">
            {date?.startDate &&
              moment(date.startDate).format(FORMAT_DATE.SHORT_MONTH_DATE)}
          </p>
          <p className="table-column-date-range">
            {date?.endDate &&
              moment(date.endDate).format(FORMAT_DATE.SHORT_MONTH_DATE)}
          </p>
        </>
      ) : null,
    filters: [
      { text: 'Male', value: 'male' },
      { text: 'Female', value: 'female' },
    ],
  },
  {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
    filterDropdown: FilterStatusColumnTableHome,
    render: StatusTagColumnTable,
    width: '20%',
  },
  {
    title: 'Action',
    key: 'action',
    width: '10%',
    render: (record: any) => {
      const scaffold_view_url = process.env.REACT_APP_VIEW_SCAFFOLD_URL;
      const scaffold_edit_url = process.env.REACT_APP_EDIT_SCAFFOLD_URL;
      return (
        <div className="table-column-action">
          <Space size="middle">
            <Button type="primary">
              {record?.requestId.slice(0, 3).toLowerCase() === 'scf' ? (
                <a
                  onClick={() =>
                    window.open(
                      `${scaffold_view_url}${record?.scaffoldGuid}`,
                      '_blank',
                    )
                  }
                >
                  View
                </a>
              ) : (
                <Link
                  to={`/insulation/view/${record?.requestId}?type=${
                    Object.values(REQUEST_TYPE)?.[record?.service - 1]
                  }`}
                >
                  View
                </Link>
              )}
              {/* <Link
                to={
                  record?.requestId.slice(0, 3).toLowerCase() === 'scf'
                    ? 'https://sisfolds.petronas.com/scaffold-request/list-scaffold-request'
                    : `/insulation/view/${record?.requestId}?type=${
                        Object.values(REQUEST_TYPE)?.[record?.service - 1]
                      }`
                }
                target={
                  record?.requestId.slice(0, 3).toLowerCase() === 'scf'
                    ? '_blank'
                    : '_self'
                }
              >
                View
              </Link> */}
            </Button>

            {record?.requestId.slice(0, 3).toLowerCase() === 'scf' ? (
              <Button>
                <a
                  onClick={() =>
                    window.open(
                      `${scaffold_edit_url}${record?.scaffoldGuid}`,
                      '_blank',
                    )
                  }
                >
                  Edit
                </a>
              </Button>
            ) : (
              <ActionButtons uniqueKey={record?.requestId} hotReload>
                {actionProps => (
                  <ActionButtonTable
                    record={record}
                    roleButton={RoleButton()}
                    actionProps={actionProps}
                  />
                )}
              </ActionButtons>
            )}
          </Space>
        </div>
      );
    },
  },
];

export const columnsUserManagement = [
  {
    title: 'Name and Email',
    dataIndex: 'nameAndEmail',
    width: '30%',
    key: 'nameAndEmail',
    sorter: true,
    render: (text: any) => {
      return (
        <>
          <p className="table-column-text-bold">{text?.name}</p>
          <p className="table-column-section-label">{text?.email}</p>
        </>
      );
    },
  },
  {
    title: 'Role',
    dataIndex: 'listRole',
    key: 'listRole',
    filterDropdown: FilterRoleUserManagement,
    render: (role: any) => (
      <>{role && role?.map((item: any) => <div>{item}</div>)}</>
    ),
  },
  {
    title: 'Last Login',
    dataIndex: 'lastLoginTime',
    key: 'lastLoginTime',
    width: '20%',
    sorter: true,
    render: (date: any) =>
      date ? (
        <p className="table-column-date-range">
          {date && moment(date).format(FORMAT_DATE.FULL_DATETIME_HOUR)}
        </p>
      ) : null,
  },
  {
    title: 'Action',
    key: 'action',
    width: '15%',
    render: (record: any) => (
      <div className="table-column-action">
        <Space size="middle">
          <ActionButtons uniqueKey={record?.userId} hotReload>
            {(actionProps: any) => (
              <EditDeleteButtonsTable
                record={record}
                actionProps={actionProps}
              />
            )}
          </ActionButtons>
        </Space>
      </div>
    ),
  },
];

export const columnsServiceProvider = [
  {
    title: 'Company name',
    dataIndex: 'companyName',
    width: '30%',
    key: 'companyName',
    sorter: true,
    render: (text: any) => {
      return (
        <>
          <p className="table-column-text-bold">{text}</p>
        </>
      );
    },
  },
  {
    title: 'Applicable service',
    dataIndex: 'applicableServicestext',
    key: 'applicableServicestext',
    render: (applicableServicestext: any) => (
      <>
        {applicableServicestext &&
          applicableServicestext?.map((item: any) => <div>{item}</div>)}
      </>
    ),
  },
  {
    title: 'Area',
    dataIndex: 'areastext',
    key: 'areastext',
    width: '20%',
    render: (areastext: any) => (
      <>
        {areastext?.length <= 3 ? (
          areastext?.map((item: any) => <div>{item}</div>)
        ) : (
          <Tooltip
            placement="bottom"
            title={areastext?.map((item: any, index: any) => (
              <span>
                {item}
                {index < areastext?.length - 1 ? `, ` : ''}
              </span>
            ))}
          >
            {areastext?.length} AREAS &nbsp;&nbsp;
            <InfoCircleOutlined />
          </Tooltip>
        )}
      </>
    ),
  },
  {
    title: 'Action',
    key: 'action',
    width: '15%',
    render: (record: any) => (
      <div className="table-column-action">
        <Space size="middle">
          <ActionButtons uniqueKey={record?.requestId} hotReload>
            {(actionProps: any) => (
              <EditDeleteButtonsTable
                record={record}
                isService={true}
                actionProps={actionProps}
              />
            )}
          </ActionButtons>
        </Space>
      </div>
    ),
  },
];

const RoleButton = () => {
  const [auth] = useAuth();
  const role = auth?.getUserRoles();
  const canEditDraf = auth?.checkRole('REQUEST_DRAFF', 'EDITED');
  const canCancelDraf = auth?.checkRole('REQUEST_DRAFF', 'CANCELLED');
  const canEditValidation = auth?.checkRole('PENDING_VALIDTION', 'EDITED');
  const canCancelValidation = auth?.checkRole('PENDING_VALIDTION', 'CANCELLED');
  const canCancelProposal = auth?.checkRole('PENDING_PROPOSAL', 'CANCELLED');
  const canCancelRemoval = auth?.checkRole('PENDING_REMOVAL', 'CANCELLED');
  const canCancelReinstatement = auth?.checkRole(
    'PENDING_REINSTATEMENT',
    'CANCELLED',
  );
  const canCancelQAQC = auth?.checkRole('PENDING_QAQC', 'CANCELLED');
  const canCancelPendingPainting = auth?.checkRole(
    'PENDING_PAINTING',
    'CANCELLED',
  );
  const canCompletedInservice = auth?.checkRole(
    'REQUEST_INSERVICE',
    'COMPLETED',
  );
  const canRejectedValidation =
    auth?.checkRole('PENDING_VALIDTION', 'REJECTED', 'INSULATION') ||
    auth?.checkRole('PENDING_VALIDTION', 'REJECTED', 'PAINTING');
  const canClosedBilling =
    auth?.checkRole('BILLING', 'CLOSED', 'INSULATION') ||
    auth?.checkRole('BILLING', 'CLOSED', 'PAINTING');
  const canRrejectedProposal = auth?.checkRole('PENDING_PROPOSAL', 'REJECTED');
  const canSendBackRemoval = auth?.checkRole('PENDING_REMOVAL', 'SENDBACK');
  const canSendBackReinstatement = auth?.checkRole(
    'PENDING_REINSTATEMENT',
    'SENDBACK',
  );
  const canSendBackPendingproposal = auth?.checkRole(
    'PENDING_PROPOSAL',
    'SENDBACK',
  );
  const canSendBackAdmin = auth?.checkRole('CLOSED', 'SENDBACK');
  const canCancelPendingqaqc = auth?.checkRole('PENDING_QAQC', 'CANCELLED');
  return {
    role,
    showHideMenmuItem: {
      canEditDraf,
      canCancelDraf,
      canEditValidation,
      canCancelValidation,
      canCancelProposal,
      canCancelRemoval,
      canCancelReinstatement,
      canCancelQAQC,
      canCompletedInservice,
      canRejectedValidation,
      canSendBackReinstatement,
      canSendBackRemoval,
      canClosedBilling,
      canRrejectedProposal,
      canCancelPendingPainting,
      canCancelPendingqaqc,
      canSendBackPendingproposal,
      canSendBackAdmin,
    },
  };
};

export const handleValidateChecklist = (
  data: any,
  textOption: [string, string][],
) => {
  const keyOfChecklists = textOption.map((i: [string, string]) => i[0]);
  const keyToValidates = [
    ...keyOfChecklists,
    'overallAcceptance',
    'inspectionDate',
  ];
  for (const u of keyToValidates) {
    if (
      !Object.keys(data).includes(u) ||
      data[u] === undefined ||
      data[u] === null
    ) {
      return true;
    }
  }
  return false;
};
